/**
 * The mech roster with the comparison panel, shared by the practice lobby and the online queue.
 * The caller supplies the heading, the intro line and the panel's footer (the controls that
 * differ between practice and the queue). Pure presentation: no navigation, no renderer. The
 * comparison panel is exported on its own for the hangar showcase (Phase 6).
 */
import { getAbility, getWeapon, MECH_LIST, type MechDefinition } from '@steelwake/shared/data';
import { colors, fonts, radii, spacing } from '@steelwake/ui';
import type { ReactNode } from 'react';

import { TOUCH_ONLY, useMediaQuery } from '../../useMediaQuery.js';

export const CLASS_LABELS = { light: 'Light', medium: 'Medium', heavy: 'Heavy' } as const;
export const CLASS_ORDER = ['light', 'medium', 'heavy'] as const;

interface Stat {
  key: string;
  label: string;
  value: (m: MechDefinition) => number;
  format: (v: number) => string;
}

/** Rough damage per second of the signature weapon (beams are per second already). */
function signatureDps(m: MechDefinition): number {
  const w = getWeapon(m.weapons[0]!.id);
  if (w.kind === 'beam') return w.damage;
  const perShot = w.damage * (w.hitscan?.pellets ?? 1) + (w.projectile?.splash?.damage ?? 0);
  return perShot * w.fireRate * (w.burst ? w.burst.count : 1);
}

const STATS: Stat[] = [
  { key: 'hp', label: 'Hull', value: (m) => m.hp, format: (v) => String(Math.round(v)) },
  { key: 'shield', label: 'Shield', value: (m) => m.shield, format: (v) => String(Math.round(v)) },
  {
    key: 'armor',
    label: 'Armour',
    value: (m) => (m.armor.legs + m.armor.torso + m.armor.arms) / 3,
    format: (v) => `${String(Math.round(v * 100))} %`,
  },
  { key: 'speed', label: 'Speed', value: (m) => m.speed, format: (v) => `${v.toFixed(1)} m/s` },
  { key: 'dps', label: 'Damage / s', value: signatureDps, format: (v) => String(Math.round(v)) },
];

/** The roster-wide maximum per stat, so every bar is on the same scale. */
const MAXIMA: Record<string, number> = Object.fromEntries(
  STATS.map((s) => [s.key, Math.max(...MECH_LIST.map((m) => s.value(m)))]),
);

function StatBar({ stat, mech, max }: { stat: Stat; mech: MechDefinition; max: number }) {
  const v = stat.value(mech);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '90px 1fr 70px',
        gap: spacing.sm,
        alignItems: 'center',
        fontSize: 13,
      }}
    >
      <span style={{ color: colors.textMuted }}>{stat.label}</span>
      <div
        style={{ height: 8, background: colors.border, borderRadius: radii.sm, overflow: 'hidden' }}
      >
        <div
          data-testid={`stat-${stat.key}`}
          style={{
            width: `${String(Math.round((v / max) * 100))}%`,
            height: '100%',
            background: mech.accent,
          }}
        />
      </div>
      <span style={{ textAlign: 'right' }}>{stat.format(v)}</span>
    </div>
  );
}

export interface MechComparePanelProps {
  mech: MechDefinition;
  /** The controls under the comparison. */
  footer: ReactNode;
  /** Sticks to the top of a scrolling page (the lobby); off inside a fixed panel (the hangar). */
  sticky?: boolean;
}

/** The selected mech's lore, stat bars and kit, with the caller's controls underneath. */
export function MechComparePanel({ mech, footer, sticky = true }: MechComparePanelProps) {
  const ability = getAbility(mech.abilities[0]!.id);
  const ultimate = getAbility(mech.ultimate);
  const weapon = getWeapon(mech.weapons[0]!.id);
  return (
    <aside
      data-testid="mech-compare"
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
        padding: spacing.lg,
        ...(sticky ? { position: 'sticky', top: spacing.lg } : {}),
      }}
    >
      <h2
        style={{ margin: 0, fontSize: 20, letterSpacing: '0.08em', color: mech.accent }}
        data-testid="selected-name"
      >
        {mech.name}
      </h2>
      <div style={{ color: colors.textMuted, fontSize: 13, marginBottom: spacing.md }}>
        {CLASS_LABELS[mech.class]} · {mech.role}
      </div>
      <p style={{ fontSize: 13, margin: `0 0 ${spacing.md}` }}>{mech.lore}</p>
      <div style={{ display: 'grid', gap: 6, marginBottom: spacing.md }}>
        {STATS.map((s) => (
          <StatBar key={s.key} stat={s} mech={mech} max={MAXIMA[s.key] ?? 1} />
        ))}
      </div>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: `4px ${spacing.sm}`,
          fontSize: 13,
          margin: `0 0 ${spacing.md}`,
        }}
      >
        <dt style={{ color: colors.textMuted }}>Weapon</dt>
        <dd style={{ margin: 0 }}>
          {weapon.name} ({weapon.kind})
        </dd>
        <dt style={{ color: colors.textMuted }}>Ability (Q)</dt>
        <dd style={{ margin: 0 }}>
          {ability.name} · {ability.description}
        </dd>
        <dt style={{ color: colors.textMuted }}>Ultimate (E)</dt>
        <dd style={{ margin: 0 }}>
          {ultimate.name} · {ultimate.description}
        </dd>
      </dl>
      {footer}
    </aside>
  );
}

export interface MechCardProps {
  mech: MechDefinition;
  active: boolean;
  onSelect: (mechId: string) => void;
  /** A line under the name (ownership, price); by default the starters are marked. */
  note?: ReactNode;
  compact?: boolean;
}

export function MechCard({ mech, active, onSelect, note, compact = false }: MechCardProps) {
  const shown =
    note !== undefined ? (
      note
    ) : mech.starter ? (
      <span style={{ color: colors.accent }}>starter</span>
    ) : null;
  const edge = active ? mech.accent : colors.border;
  return (
    <button
      type="button"
      data-testid={`mech-card-${mech.id}`}
      aria-pressed={active}
      onClick={() => onSelect(mech.id)}
      style={{
        textAlign: 'left',
        padding: compact ? `6px ${spacing.sm}` : spacing.sm,
        background: active ? colors.surface : 'transparent',
        borderStyle: 'solid',
        borderWidth: '1px 1px 1px 4px',
        borderColor: `${edge} ${edge} ${edge} ${mech.accent}`,
        borderRadius: radii.sm,
        color: colors.text,
        cursor: 'pointer',
        fontFamily: fonts.ui,
      }}
    >
      <div style={{ fontWeight: 700, letterSpacing: '0.06em', fontSize: compact ? 13 : 14 }}>
        {mech.name}
      </div>
      {compact ? null : <div style={{ fontSize: 12, color: colors.textMuted }}>{mech.role}</div>}
      {shown !== null && shown !== undefined ? (
        <div data-testid={`mech-note-${mech.id}`} style={{ fontSize: 11, marginTop: 4 }}>
          {shown}
        </div>
      ) : null}
    </button>
  );
}

export interface MechPickerProps {
  /** The selected mech id. */
  value: string;
  onChange: (mechId: string) => void;
  title: string;
  intro: ReactNode;
  /** The controls under the comparison panel. */
  footer: ReactNode;
  /** A line under each card (ownership, price); by default the starters are marked. */
  cardNote?: (mech: MechDefinition) => ReactNode;
  testId?: string;
}

export function MechPicker({
  value,
  onChange,
  title,
  intro,
  footer,
  cardNote,
  testId,
}: MechPickerProps) {
  // A phone or tablet without a mouse: the arena needs a keyboard and mouse or a gamepad.
  const touchOnly = useMediaQuery(TOUCH_ONLY);
  const selected = MECH_LIST.find((m) => m.id === value) ?? MECH_LIST[0]!;
  return (
    <div
      data-testid={testId ?? 'mech-picker'}
      style={{ width: 'min(1100px, 96vw)', fontFamily: fonts.ui, color: colors.text }}
    >
      <h1 style={{ margin: `0 0 ${spacing.sm}`, fontSize: 22, letterSpacing: '0.12em' }}>
        {title}
      </h1>
      <p style={{ margin: `0 0 ${spacing.lg}`, color: colors.textMuted, fontSize: 14 }}>{intro}</p>
      {touchOnly ? (
        <p
          data-testid="desktop-only"
          role="note"
          style={{
            margin: `0 0 ${spacing.lg}`,
            padding: '8px 12px',
            border: `1px solid ${colors.warning}`,
            borderRadius: radii.sm,
            color: colors.warning,
            fontSize: 13,
          }}
        >
          The arena is desktop only for now: it needs a keyboard and mouse or a gamepad. The hangar
          and the leaderboards work here.
        </p>
      ) : null}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 360px',
          gap: spacing.lg,
          alignItems: 'start',
        }}
      >
        <div>
          {CLASS_ORDER.map((cls) => (
            <section key={cls} style={{ marginBottom: spacing.lg }}>
              <h2
                style={{
                  margin: `0 0 ${spacing.sm}`,
                  fontSize: 12,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                {CLASS_LABELS[cls]}
              </h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: spacing.sm,
                }}
              >
                {MECH_LIST.filter((m) => m.class === cls).map((m) => (
                  <MechCard
                    key={m.id}
                    mech={m}
                    active={m.id === value}
                    onSelect={onChange}
                    {...(cardNote ? { note: cardNote(m) } : {})}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
        <MechComparePanel mech={selected} footer={footer} />
      </div>
    </div>
  );
}
